import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationPermission,
  ProductPermission,
  PropertyPermission,
  UserPermission,
} from 'src/modules/shared/types/permissions.type';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useDebounce } from '@uidotdev/usehooks';
import { AnimatePresence, motion } from 'framer-motion';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import {
  PiBuildingLight,
  PiCaretDown,
  PiCaretUp,
  PiDotsThreeVertical,
  PiGlobeHemisphereWestLight,
  PiPlus,
  PiUploadSimple,
} from 'react-icons/pi';
import { useProperties } from 'src/modules/shared/hooks/api/catalog/properties';
import { useUpdateProperty } from 'src/modules/shared/hooks/api/catalog/properties.mutation';
import { PropertiesTable } from '../organisms/properties-table';
import { PropertiesList } from '../organisms/properties-list';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PropertyFilters } from '../molecules/property-filters';
import {
  type PropertyFiltersState,
  type PropertySortBy,
} from '../../types/property-filters.types';
import type { CatalogProperty } from 'src/api/generated';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import {
  type PropertiesSearch,
  propertiesSearchSchema,
} from '../../schemas/properties-search.schema';
import { toPropertyPublicationsSearch } from '../../schemas/property-publications-search.schema';
import { Route as CatalogPropertiesRoute } from 'src/routes/catalog/properties.index';
import { Route as CatalogPropertiesCreateRoute } from 'src/routes/catalog/properties.create';
import { Route as CatalogPropertiesEditRoute } from 'src/routes/catalog/properties.$propertyId.edit';
import { Route as CatalogPropertiesPublicationsRoute } from 'src/routes/catalog/properties.publications';
import { PropertyImportModal } from '../molecules/property-import-modal';
import { useAlert } from 'src/contexts/alert-context';
import {
  useLocalities,
  useRegions,
} from 'src/modules/shared/hooks/api/location/location';
import {
  PropertyActiveFilters,
  type PropertyActiveFilterItem,
} from '../molecules/property-active-filters';
import { PropertiesToolbar } from '../molecules/properties-toolbar';
import { removeUndefinedSearchParams } from '../../utils/property-search.util';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { SplitButtonDropdown } from 'src/modules/shared/components/molecules/dropdowns/split-button-dropdown';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { trimToUndefined } from 'src/modules/shared/utils/formatting/text.util';
import {
  buildActivePropertyFilterItems,
  buildPropertiesQueryParams,
} from '../../utils/properties-page.util';

const SEARCH_DEBOUNCE_MS = 300;

export const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canUpdateAll } = useHasPermissions(
    PropertyPermission.Update
  );
  const { hasPermission: canUpdateScoped } = useHasPermissions(
    PropertyPermission.UpdateScoped
  );
  const { hasPermission: canCreate } = useHasPermissions(
    PropertyPermission.Create
  );
  const { hasPermission: canReadUsers } = useHasPermissions(
    UserPermission.Read
  );
  const { user } = useCurrentUser();
  const canRead = canReadAll || canReadScoped;
  const { hasPermission: canImport } = useHasPermissions(
    ProductPermission.Create
  );
  const { hasPermission: canReadIntegrations } = useHasPermissions(
    IntegrationPermission.Read
  );
  const {
    phoneVersion = 'v1',
    search: searchTerm = '',
    operation = 'ALL',
    activeStatus = 'ACTIVE',
    sortBy = 'UPDATED_DESC',
    viewMode = 'VISUAL',
    pageSize = 20,
    userId,
    salePriceMin,
    salePriceMax,
    rentPriceMin,
    rentPriceMax,
    region,
    locality,
  } = useSearch({
    from: CatalogPropertiesRoute.id,
    select: (search: PropertiesSearch) => ({
      search: search.search,
      phoneVersion: search.phoneVersion,
      operation: search.operation,
      activeStatus: search.activeStatus,
      sortBy: search.sortBy,
      viewMode: search.viewMode,
      pageSize: search.pageSize,
      userId: search.userId,
      salePriceMin: search.salePriceMin,
      salePriceMax: search.salePriceMax,
      rentPriceMin: search.rentPriceMin,
      rentPriceMax: search.rentPriceMax,
      region: search.region,
      locality: search.locality,
    }),
  });

  const { t } = useI18n();
  const router = useRouter();
  const { showAlert } = useAlert();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(true);
  const [localSearchValue, setLocalSearchValue] = useState(searchTerm);
  const [propertyToDeactivate, setPropertyToDeactivate] =
    useState<CatalogProperty | null>(null);
  const debouncedSearchValue = useDebounce(
    localSearchValue,
    SEARCH_DEBOUNCE_MS
  );
  const { regions } = useRegions();
  const { localities } = useLocalities();

  const filters = useMemo<PropertyFiltersState>(() => {
    return {
      search: searchTerm,
      operation,
      activeStatus,
      sortBy,
      viewMode,
      userId,
      salePriceMin,
      salePriceMax,
      rentPriceMin,
      rentPriceMax,
      region,
      locality,
    };
  }, [
    searchTerm,
    operation,
    activeStatus,
    sortBy,
    viewMode,
    userId,
    salePriceMin,
    salePriceMax,
    rentPriceMin,
    rentPriceMax,
    region,
    locality,
  ]);

  const editorSearch = useMemo(
    () =>
      propertiesSearchSchema.parse({
        phoneVersion,
        search: searchTerm || undefined,
        operation,
        activeStatus,
        sortBy,
        viewMode,
        pageSize,
        userId,
        salePriceMin,
        salePriceMax,
        rentPriceMin,
        rentPriceMax,
        region,
        locality,
      }),
    [
      activeStatus,
      locality,
      operation,
      pageSize,
      phoneVersion,
      sortBy,
      region,
      rentPriceMax,
      rentPriceMin,
      salePriceMax,
      salePriceMin,
      searchTerm,
      userId,
      viewMode,
    ]
  );

  const queryParams = useMemo(() => {
    return buildPropertiesQueryParams(filters, pageSize);
  }, [filters, pageSize]);

  const {
    properties,
    totalCount,
    loading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProperties({ ...queryParams, enabled: canRead });

  const { updateProperty, loading: updatingProperty } = useUpdateProperty({
    onSuccess: () => {
      showAlert(t('catalog.properties.alert.update.success'), 'success');
    },
    onError: () => {
      showAlert(t('catalog.properties.alert.update.error'), 'error');
    },
  });

  const { updateProperty: deactivateProperty, loading: deactivatingProperty } =
    useUpdateProperty({
      onSuccess: () => {
        showAlert(t('catalog.properties.alert.deactivate.success'), 'success');
      },
      onError: () => {
        showAlert(t('catalog.properties.alert.deactivate.error'), 'error');
      },
    });

  const handleNavigateSearch = useCallback(
    (updater: (prev: PropertiesSearch) => PropertiesSearch) => {
      void navigate({
        to: CatalogPropertiesRoute.to,
        search: (prev) =>
          removeUndefinedSearchParams(
            updater(propertiesSearchSchema.parse(prev))
          ),
      });
    },
    [navigate]
  );

  const buildSearchHref = useCallback(
    (nextSearch: PropertiesSearch) =>
      router.buildLocation({
        to: CatalogPropertiesRoute.to,
        search: removeUndefinedSearchParams(nextSearch),
      }).href,
    [router]
  );

  useEffect(() => {
    setLocalSearchValue(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const normalizedNext = trimToUndefined(debouncedSearchValue);
    const normalizedCurrent = trimToUndefined(searchTerm);

    if (normalizedNext === normalizedCurrent) {
      return;
    }

    handleNavigateSearch((prev) => ({
      ...prev,
      search: normalizedNext,
    }));
  }, [debouncedSearchValue, handleNavigateSearch, searchTerm]);

  const handleFiltersChange = (nextFilters: PropertyFiltersState) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      search: trimToUndefined(nextFilters.search),
      operation: nextFilters.operation,
      activeStatus: nextFilters.activeStatus,
      sortBy: nextFilters.sortBy,
      viewMode: nextFilters.viewMode,
      userId: nextFilters.userId,
      salePriceMin: nextFilters.salePriceMin,
      salePriceMax: nextFilters.salePriceMax,
      rentPriceMin: nextFilters.rentPriceMin,
      rentPriceMax: nextFilters.rentPriceMax,
      region: trimToUndefined(nextFilters.region),
      locality: trimToUndefined(nextFilters.locality),
    }));
  };

  const handleClearFilters = () => {
    setLocalSearchValue('');
    handleNavigateSearch((prev) => ({
      ...prev,
      search: undefined,
      operation: 'ALL',
      activeStatus: 'ACTIVE',
      sortBy: prev.sortBy,
      viewMode: prev.viewMode,
      userId: undefined,
      salePriceMin: undefined,
      salePriceMax: undefined,
      rentPriceMin: undefined,
      rentPriceMax: undefined,
      region: undefined,
      locality: undefined,
    }));
  };

  const clearAllHref = useMemo(
    () =>
      buildSearchHref({
        phoneVersion,
        operation: 'ALL',
        activeStatus: 'ACTIVE',
        sortBy,
        viewMode,
        pageSize,
      }),
    [buildSearchHref, pageSize, phoneVersion, sortBy, viewMode]
  );

  const handleSortChange = (nextSortBy: PropertySortBy) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      sortBy: nextSortBy,
    }));
  };

  const handleOperationChange = (
    nextOperation: PropertiesSearch['operation']
  ) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      operation: nextOperation,
    }));
  };

  const handleActiveStatusChange = (
    nextActiveStatus: PropertiesSearch['activeStatus']
  ) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      activeStatus: nextActiveStatus,
    }));
  };

  const handleViewModeChange = (nextViewMode: PropertiesSearch['viewMode']) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      viewMode: nextViewMode,
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      pageSize: newSize,
    }));
  };

  const handleOpenCreate = useCallback(() => {
    void navigate({
      to: CatalogPropertiesCreateRoute.to,
      search: editorSearch,
    });
  }, [editorSearch, navigate]);

  const handleOpenPublications = useCallback(() => {
    void navigate({
      to: CatalogPropertiesPublicationsRoute.to,
      search: toPropertyPublicationsSearch(editorSearch),
    });
  }, [editorSearch, navigate]);

  const handleEditProperty = (property: CatalogProperty) => {
    void navigate({
      to: CatalogPropertiesEditRoute.to,
      params: { propertyId: property.id },
      search: editorSearch,
    });
  };

  const handleDeactivateRequest = (property: CatalogProperty) => {
    if (
      !canUpdateAll &&
      !(canUpdateScoped && user != null && property.userId === user.id)
    ) {
      return;
    }
    setPropertyToDeactivate(property);
  };

  const { users, loading: loadingUsers } = useAllUsers(
    createQuery().equals('kind', 'person').build(),
    canReadUsers
  );
  const regionLabelByCode = useMemo(
    () => new Map(regions.map((item) => [item.code, item.name])),
    [regions]
  );
  const localityLabelByCode = useMemo(
    () => new Map(localities.map((item) => [item.code, item.name])),
    [localities]
  );
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users]
  );

  const handleUserAssign = async (propertyId: number, userId: number | null) =>
    updateProperty({ id: propertyId, userId });

  const handleConfirmDeactivate = async () => {
    if (!propertyToDeactivate) return;

    await deactivateProperty({
      id: propertyToDeactivate.id,
      isActive: false,
    });
    setPropertyToDeactivate(null);
  };

  const handleLoadMore = () => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  };

  const handleRetry = () => {
    void refetch();
  };

  const activeFilterItems = useMemo<PropertyActiveFilterItem[]>(() => {
    return buildActivePropertyFilterItems({
      activeStatus,
      buildSearchHref,
      locality,
      localityLabelByCode,
      operation,
      pageSize,
      phoneVersion,
      region,
      regionLabelByCode,
      rentPriceMax,
      rentPriceMin,
      salePriceMax,
      salePriceMin,
      searchTerm,
      sortBy,
      t,
      userId,
      usersById,
      viewMode,
    });
  }, [
    activeStatus,
    buildSearchHref,
    locality,
    localityLabelByCode,
    operation,
    pageSize,
    phoneVersion,
    region,
    regionLabelByCode,
    rentPriceMax,
    rentPriceMin,
    salePriceMax,
    salePriceMin,
    searchTerm,
    sortBy,
    t,
    userId,
    usersById,
    viewMode,
  ]);

  const headerActionItems = useMemo(
    () =>
      [
        {
          content: t('catalog.properties.publications.page.title'),
          icon: <PiGlobeHemisphereWestLight className="h-4 w-4" />,
          onClick: handleOpenPublications,
        },
        {
          content: t('catalog.properties.import.button'),
          icon: <PiUploadSimple className="h-4 w-4" />,
          onClick: () => setIsImportOpen(true),
        },
        {
          content: t('catalog.properties.new'),
          icon: <PiPlus className="h-4 w-4" />,
          onClick: handleOpenCreate,
        },
      ].filter((_item, index) =>
        index === 0
          ? canReadIntegrations && canRead
          : index === 1
            ? canImport
            : canCreate
      ),
    [
      canImport,
      canReadIntegrations,
      canCreate,
      canRead,
      handleOpenCreate,
      handleOpenPublications,
      t,
    ]
  );
  const toolbarToggleLabel = isToolbarExpanded
    ? t('catalog.properties.toolbar.hideToolbar')
    : t('catalog.properties.toolbar.showToolbar');
  const toolbarToggleButton = (
    <Tooltip content={toolbarToggleLabel} size="sm">
      <Button
        type="button"
        variant="primary"
        appearance="soft"
        size="md"
        isIconOnly
        aria-label={toolbarToggleLabel}
        onClick={() => setIsToolbarExpanded((currentValue) => !currentValue)}
      >
        {isToolbarExpanded ? (
          <PiCaretUp className="h-4 w-4" />
        ) : (
          <PiCaretDown className="h-4 w-4" />
        )}
      </Button>
    </Tooltip>
  );

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex flex-1 items-center justify-center p-6">
          <ErrorState
            title={t('catalog.properties.error.title')}
            description={t('catalog.properties.error.description')}
            onAction={handleRetry}
            actionContent={t('catalog.products.error.retry')}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="sticky top-0 z-20 border-b border-slate-100 bg-white px-4 py-1">
            <TokenPagination
              className="bg-white"
              pageSize={pageSize}
              currentCount={properties.length}
              totalCount={totalCount}
              onPageSizeChange={handlePageSizeChange}
              onNextPage={handleLoadMore}
              hasNextPage={hasNextPage}
              isLoading={isFetchingNextPage}
              endAddon={toolbarToggleButton}
            />
          </div>
          <div className="px-4 pb-8 pt-4">
            {viewMode === 'COMPACT' ? (
              <PropertiesTable
                properties={properties}
                loading={loading}
                isLoadingMore={isFetchingNextPage}
                users={users}
                usersLoading={loadingUsers}
                onUserAssign={handleUserAssign}
                isAssigning={updatingProperty}
                onEdit={handleEditProperty}
                onDeactivate={handleDeactivateRequest}
              />
            ) : (
              <PropertiesList
                properties={properties}
                loading={loading}
                isLoadingMore={isFetchingNextPage}
                users={users}
                usersLoading={loadingUsers}
                onUserAssign={handleUserAssign}
                isAssigning={updatingProperty}
                onEdit={handleEditProperty}
                onDeactivate={handleDeactivateRequest}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <Panel className="flex flex-col h-full">
        <PanelHeader
          className="px-4 py-3 border-b"
          containerClassName="gap-y-3"
          childrenClassName="min-w-0"
          actionsClassName="ml-auto self-center"
          actions={
            <div className="flex items-center gap-3">
              <div className="flex items-center sm:hidden">
                <MenuDropdown
                  items={headerActionItems}
                  placement="bottom-end"
                  handlerComponent={
                    <Button
                      type="button"
                      variant="secondary"
                      appearance="outline"
                      size="md"
                      isIconOnly
                      aria-label={t('catalog.properties.header.actionsMenu')}
                    >
                      <PiDotsThreeVertical className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>

              <div className="hidden sm:flex sm:flex-row sm:items-center gap-4">
                {canReadIntegrations && canRead && (
                  <Button
                    variant="secondary"
                    appearance="outline"
                    size="md"
                    onClick={handleOpenPublications}
                    className="flex items-center justify-center gap-2"
                  >
                    <PiGlobeHemisphereWestLight className="w-4 h-4" />
                    <span className="whitespace-nowrap">
                      {t('catalog.properties.publications.page.title')}
                    </span>
                  </Button>
                )}
                {canCreate &&
                  (canImport ? (
                    <SplitButtonDropdown
                      label={t('catalog.properties.new')}
                      onClick={handleOpenCreate}
                      icon={<PiPlus className="w-4 h-4 flex-shrink-0" />}
                      items={[
                        {
                          content: t('catalog.properties.import.button'),
                          icon: <PiUploadSimple className="h-4 w-4" />,
                          onClick: () => setIsImportOpen(true),
                        },
                      ]}
                      variant="primary"
                      appearance="filled"
                      className="min-w-[2.5rem]"
                      mainButtonClassName="gap-2 whitespace-nowrap px-3"
                    />
                  ) : (
                    <Button
                      variant="primary"
                      appearance="filled"
                      size="md"
                      onClick={handleOpenCreate}
                    >
                      <PiPlus className="w-4 h-4" />
                      {t('catalog.properties.new')}
                    </Button>
                  ))}
                {canImport && !canCreate && (
                  <Button
                    variant="primary"
                    appearance="filled"
                    size="md"
                    onClick={() => setIsImportOpen(true)}
                  >
                    <PiUploadSimple className="w-4 h-4" />
                    {t('catalog.properties.import.button')}
                  </Button>
                )}
              </div>
            </div>
          }
        >
          <div className="flex items-center gap-4">
            <PiBuildingLight className="w-7 h-7 flex-shrink-0" />
            <Text as="h2" size="h2" weight="medium">
              {t('catalog.properties.title')}
            </Text>
          </div>
        </PanelHeader>

        <AnimatePresence initial={false}>
          {isToolbarExpanded && (
            <motion.div
              key="properties-toolbar"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <PageFiltersBar>
                <PropertiesToolbar
                  activeFilterCount={activeFilterItems.length}
                  operation={operation}
                  activeStatus={activeStatus}
                  isExpanded={isToolbarExpanded}
                  searchValue={localSearchValue}
                  sortBy={sortBy}
                  viewMode={viewMode}
                  onOpenMobileFilters={() => setIsMobileFiltersOpen(true)}
                  onOperationChange={handleOperationChange}
                  onActiveStatusChange={handleActiveStatusChange}
                  onSearchChange={setLocalSearchValue}
                  onSortChange={handleSortChange}
                  onViewModeChange={handleViewModeChange}
                />
              </PageFiltersBar>
            </motion.div>
          )}
        </AnimatePresence>

        <PropertyActiveFilters
          items={activeFilterItems}
          clearAllHref={clearAllHref}
        />

        {renderContent()}
      </Panel>

      {canImport && (
        <PropertyImportModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
        />
      )}

      <Modal
        isOpen={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        header={
          <Text as="h3" size="h5" weight="semibold">
            {t('catalog.properties.toolbar.filters')}
          </Text>
        }
        dialogClassName="!max-w-sm"
        bodyClassName="p-0"
        body={
          <PropertyFilters
            filters={filters}
            onChange={handleFiltersChange}
            onClear={handleClearFilters}
            users={users}
            usersLoading={loadingUsers}
            className="border-0 bg-white"
            showSearch={false}
            layout="stacked"
          />
        }
      />

      <ConfirmModal
        isOpen={Boolean(propertyToDeactivate)}
        onClose={() => setPropertyToDeactivate(null)}
        onConfirm={handleConfirmDeactivate}
        confirmText={t('catalog.properties.confirmDeactivate.confirm')}
        cancelText={t('catalog.properties.confirmDeactivate.cancel')}
        loading={deactivatingProperty}
        variant="danger"
        title={t('catalog.properties.confirmDeactivate.title')}
      >
        <Text>
          {t('catalog.properties.confirmDeactivate.messagePrefix')}{' '}
          <strong>
            {propertyToDeactivate?.name ??
              t('catalog.properties.confirmDeactivate.fallbackName')}
          </strong>
          {t('catalog.properties.confirmDeactivate.messageSuffix')}
        </Text>
        <Text className="mt-3">
          {t('catalog.properties.confirmDeactivate.publicationImpact')}
        </Text>
      </ConfirmModal>
    </div>
  );
};
